/** Range Planning "Items" list (Demo Mode only). Modelled on the legacy Items screen shell
 * (search + filter + table + create) and on RecipeV2's list page, but backed entirely by the
 * new rangePlanningItemsClient mocked API - see src/app/services/api/rangePlanningItems. */

import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  MenuItem,
  SvgIcon,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import PageArea from '../../components/shared/PageArea';
import PageTitle from '../../components/shared/PageTitle';
import { Section } from '../../components/ui/Section';
import { TableHeader } from '../../components/forms/TableHeader';
import { Pill } from '../../components/shared/Pill';
import CustomPagination from '../../components/forms/Pagination';
import { Plus, SearchLg } from '../../assets';
import useRangePlanningItemsList from './hooks/useRangePlanningItemsList';
import useRangePlanningItemsApi from './hooks/useRangePlanningItemsApi';
import CarbonRatingChip from '../RecipeV2/components/CarbonRatingChip';
import { ItemLifecycleStatus } from './types';

const QuickFilterDivider = () => (
  <div style={{ width: 1, height: 24, backgroundColor: '#E0E0E0', margin: '0 8px' }} />
);

const statusColor: Record<ItemLifecycleStatus, 'success' | 'warning' | 'default'> = {
  [ItemLifecycleStatus.Active]: 'success',
  [ItemLifecycleStatus.PendingReview]: 'warning',
  [ItemLifecycleStatus.Inactive]: 'default',
};

const CATEGORIES = ['Meat & Poultry', 'Dry Goods', 'Dairy', 'Fresh Produce'];
const PAGE_SIZE = 20;

const RangePlanningItemsList = () => {
  const { t } = useTranslation('rangePlanningItems');
  const navigate = useNavigate();
  const { items, totalCount, isLoading, loadItems } = useRangePlanningItemsList();
  const { createItem } = useRangePlanningItemsApi();

  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState('');
  const [page, setPage] = useState(0);

  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [newItemName, setNewItemName] = useState('');
  const [newItemCategory, setNewItemCategory] = useState(CATEGORIES[0]);
  const [newItemUom, setNewItemUom] = useState('KG');
  const [newItemSupplier, setNewItemSupplier] = useState('');

  useEffect(() => {
    loadItems(search, category, page, PAGE_SIZE);
  }, [loadItems, search, category, page]);

  const maxPages = useMemo(() => Math.max(1, Math.ceil(totalCount / PAGE_SIZE)), [totalCount]);

  const handleSearch = () => {
    setSearch(searchInput);
    setPage(0);
  };

  const handleCreate = async () => {
    if (!newItemName.trim()) return;
    await createItem({
      name: newItemName,
      category: newItemCategory,
      baseUom: newItemUom,
      supplierName: newItemSupplier,
    });
    setShowCreateDialog(false);
    setNewItemName('');
    setNewItemSupplier('');
    loadItems(search, category, page, PAGE_SIZE);
  };

  return (
    <PageArea>
      <PageTitle>{t('title', 'Items')}</PageTitle>

      <Typography variant="textSM"
        color="text.secondary"
        sx={{ mt: 2, mb: 4 }}>
        {t('intro', 'Item master data - supplier, unit of measure, pricing, allergens, nutrition, carbon, catalogue availability, recipe usage and inventory mapping in one place.')}
      </Typography>

      {/* Quick filters + search row */}
      <Grid container
        spacing={2}
        alignItems="center"
        sx={{ mb: 4 }}>
        <Grid item
          sx={{ display: 'flex', alignItems: 'center' }}>
          <TextField
            size="small"
            label={t('search', 'Search')}
            placeholder={t('searchPlaceholder', 'Search by item number or name')}
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
            sx={{ minWidth: 260 }}
          />
        </Grid>
        <Grid item>
          <Button
            onClick={handleSearch}
            startIcon={<SvgIcon><SearchLg /></SvgIcon>}
          >
            {t('search', 'Search')}
          </Button>
        </Grid>
        <Grid item>
          <QuickFilterDivider />
        </Grid>
        <Grid item>
          <Grid container
            spacing={1}
            alignItems="center">
            <Grid item>
              <Chip
                label={t('allCategories', 'All categories')}
                color={category === '' ? 'primary' : 'default'}
                onClick={() => { setCategory(''); setPage(0); }}
              />
            </Grid>
            {CATEGORIES.map((c) => (
              <Grid item
                key={c}>
                <Chip
                  label={c}
                  color={category === c ? 'primary' : 'default'}
                  onClick={() => { setCategory((prev) => (prev === c ? '' : c)); setPage(0); }}
                />
              </Grid>
            ))}
          </Grid>
        </Grid>
      </Grid>

      <Section>
        <TableHeader>
          {t('title', 'Items')}
          <Pill text={`${totalCount} ${t('items', 'Items')}`} />
          <Box sx={{ ml: 'auto' }}>
            <Button
              onClick={() => setShowCreateDialog(true)}
              startIcon={<SvgIcon><Plus /></SvgIcon>}
            >
              {t('createItem', 'Create Item')}
            </Button>
          </Box>
        </TableHeader>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>{t('itemNumber', 'Item Number')}</TableCell>
                <TableCell>{t('name', 'Name')}</TableCell>
                <TableCell>{t('category', 'Category')}</TableCell>
                <TableCell>{t('supplier', 'Supplier')}</TableCell>
                <TableCell align="right">{t('contractPrice', 'Contract Price')}</TableCell>
                <TableCell>{t('carbon', 'Carbon')}</TableCell>
                <TableCell>{t('statusLabel', 'Status')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {!isLoading && items.length === 0 && (
                <TableRow><TableCell colSpan={7}>{t('noItems', 'No items match this filter.')}</TableCell></TableRow>
              )}
              {items.map((item) => (
                <TableRow
                  key={item.id}
                  hover
                  onClick={() => navigate(`/range-planning/items/${item.id}`)}
                  sx={{ cursor: 'pointer' }}
                >
                  <TableCell>{item.itemNumber}</TableCell>
                  <TableCell>{item.name}</TableCell>
                  <TableCell>{item.category}</TableCell>
                  <TableCell>{item.supplier.supplierName}</TableCell>
                  <TableCell align="right">{item.pricing.currency} {item.pricing.contractPrice.toFixed(2)}</TableCell>
                  <TableCell><CarbonRatingChip rating={item.carbonRating} /></TableCell>
                  <TableCell>
                    <Chip label={t(`status.${item.status}`, item.status)}
                      color={statusColor[item.status]}
                      size="small" />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        {!isLoading && (
          <CustomPagination
            page={page}
            setPage={setPage}
            maxPages={maxPages}
            breakpointForChangeDisplay={120000}
            useNoDataMessage={false}
          />
        )}
      </Section>

      <Dialog open={showCreateDialog}
        onClose={() => setShowCreateDialog(false)}
        maxWidth="sm"
        fullWidth>
        <DialogTitle>{t('createItem', 'Create Item')}</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 4, mt: 2 }}>
            <TextField
              label={t('name', 'Name')}
              value={newItemName}
              onChange={(e) => setNewItemName(e.target.value)}
              fullWidth
              required
            />
            <TextField
              select
              label={t('category', 'Category')}
              value={newItemCategory}
              onChange={(e) => setNewItemCategory(e.target.value)}
              fullWidth
            >
              {CATEGORIES.map((c) => (
                <MenuItem key={c}
                  value={c}>{c}</MenuItem>
              ))}
            </TextField>
            <TextField
              label={t('baseUom', 'Base UOM')}
              value={newItemUom}
              onChange={(e) => setNewItemUom(e.target.value)}
              fullWidth
            />
            <TextField
              label={t('supplier', 'Supplier')}
              value={newItemSupplier}
              onChange={(e) => setNewItemSupplier(e.target.value)}
              fullWidth
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button variant="secondary"
            onClick={() => setShowCreateDialog(false)}>
            {t('cancel', 'Cancel')}
          </Button>
          <Button onClick={handleCreate}
            disabled={!newItemName.trim()}>
            {t('create', 'Create')}
          </Button>
        </DialogActions>
      </Dialog>
    </PageArea>
  );
};

export default RangePlanningItemsList;
